import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  VStack,
  useColorModeValue,
  Text,
  Box,
  Checkbox,
  HStack,
  Input,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepTitle,
  StepDescription,
  StepSeparator,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
} from '@chakra-ui/react';
import { getData, postData } from '../../utils/httpClient';
import { createCourseInscription } from '../../services/api';
import { calculateInstallmentAmount } from '../../utils/paymentPlan';

const CreateInscriptionModal = ({ isOpen, onClose, onInscriptionCreated }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    studentId: '',
    studyId: '',
    studyPlanId: '',
    selectedCourses: [], // Cambiamos de selectedSubjects a selectedCourses
  });
  const [students, setStudents] = useState([]);
  const [studies, setStudies] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]); // Nueva lista de cursos disponibles
  const [selectedCoursesDetails, setSelectedCoursesDetails] = useState([]); // Detalles de cursos seleccionados
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const textColor = useColorModeValue('navy.700', 'white');
  const brandStars = useColorModeValue('brand.500', 'brand.400');

  // Definir los pasos del stepper
  const steps = [
    { 
      title: 'Selección', 
      description: 'Estudiante y Estudio',
      status: currentStep === 1 ? 'active' : currentStep > 1 ? 'complete' : 'incomplete'
    },
    { 
      title: 'Materias', 
      description: 'Materias y Pagos',
      status: currentStep === 2 ? 'active' : currentStep > 2 ? 'complete' : 'incomplete'
    }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchStudents();
      fetchStudies();
      fetchPaymentMethods();
      setCurrentStep(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.studyId && currentStep >= 2) {
      fetchSubjectsByStudy(formData.studyId);
    }
  }, [formData.studyId, currentStep]);

  const fetchStudents = async () => {
    try {
      const response = await getData('/person/student');
      console.log('Students response:', response); // Para debugging
      console.log('First student:', response.payload?.people?.[0]); // Ver estructura del primer estudiante
      const studentsData = response.payload?.people || [];
      setStudents(Array.isArray(studentsData) ? studentsData : []);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]); // Asegurar que siempre sea un array
    }
  };

  const fetchStudies = async () => {
    try {
      const response = await getData('/study');
      console.log('Studies response:', response); // Para debugging
      console.log('First study:', response.payload?.studies?.[0]); // Ver estructura del primer estudio
      const studiesData = response.payload?.studies || [];
      setStudies(Array.isArray(studiesData) ? studiesData : []);
    } catch (error) {
      console.error('Error fetching studies:', error);
      setStudies([]); // Asegurar que siempre sea un array
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await getData('/payment-methods');
      const list = Array.isArray(response)
        ? response
        : response?.payload?.paymentMethods || response?.payload || [];
      setPaymentMethods(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([]);
    }
  };

  const fetchSubjectsByStudy = async (studyId) => {
    try {
      console.log('Fetching subjects for studyId:', studyId);
      const response = await getData(`/subjects?studyId=${studyId}`);
      console.log('Subjects response:', response);
      // La respuesta tiene la estructura { payload: { subjects: [...] } }
      const subjectsData = response.payload?.subjects || [];
      console.log('Subjects data:', subjectsData);
      setAvailableSubjects(Array.isArray(subjectsData) ? subjectsData : []);
      
      // Procesar los cursos de todas las materias
      const allCourses = [];
      subjectsData.forEach(subject => {
        if (subject.courses && subject.courses.length > 0) {
          subject.courses.forEach(course => {
            allCourses.push({
              ...course,
              subject: subject, // Incluir información de la materia
              subjectName: subject.description,
              subjectCycle: subject.cycle,
            });
          });
        } else {
          // Si no hay cursos, crear uno por defecto basado en la materia
          allCourses.push({
            id: `subject-${subject.id}`, // ID temporal para materias sin cursos
            subjectId: subject.id,
            subject: subject,
            subjectName: subject.description,
            subjectCycle: subject.cycle,
            startDate: null,
            endDate: null,
            inscriptionStartDate: null,
            inscriptionEndDate: null,
            price: 0,
            coursePaymentMethods: []
          });
        }
      });
      
      console.log('Available courses:', allCourses);
      setAvailableCourses(allCourses);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setAvailableSubjects([]);
      setAvailableCourses([]);
    }
  };

  const handleContinue = () => {
    if (currentStep === 1) {
      if (!formData.studentId || !formData.studyId) {
        alert('Por favor seleccione un estudiante y un estudio');
        return;
      }
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setAvailableSubjects([]);
      setAvailableCourses([]);
      setSelectedCoursesDetails([]);
    }
  };

  const handleCoursePaymentChange = (courseId, field, value) => {
    setSelectedCoursesDetails(prev => {
      const existing = prev.find(c => c.courseId === courseId);
      if (existing) {
        return prev.map(c => c.courseId === courseId ? { ...c, [field]: value } : c);
      } else {
        return [...prev, { courseId: courseId, [field]: value }];
      }
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (formData.selectedCourses.length === 0) {
      alert('Por favor seleccione al menos un curso');
      return;
    }

    setLoading(true);
    try {
      // Preparar los datos de los cursos con información de pago
      const coursesWithPayment = formData.selectedCourses.map(courseId => {
        const courseDetail = selectedCoursesDetails ? selectedCoursesDetails.find(c => c.courseId === courseId) : null;
        const rawId = String(courseId);
        const isSubjectOnly = rawId.startsWith('subject-');
        return {
          courseId: isSubjectOnly ? undefined : parseInt(rawId, 10),
          subjectId: isSubjectOnly ? parseInt(rawId.replace('subject-', ''), 10) : undefined,
          paymentMethodId: courseDetail?.paymentMethodId || null,
          amount: courseDetail?.amount || 0,
        };
      });

      if (coursesWithPayment.some((course) => !course.paymentMethodId)) {
        alert('Elegí la modalidad de pago de cada asignatura seleccionada');
        setLoading(false);
        return;
      }

      const inscriptionData = {
        studentId: parseInt(formData.studentId),
        role: 'BEDELIA',
        courses: coursesWithPayment,
      };

      console.log('Enviando datos de inscripción de cursos:', inscriptionData);
      
      // Usar el nuevo endpoint de inscripciones de cursos
      const response = await createCourseInscription(inscriptionData);
      
      if (response.status === 201) {
        alert('Inscripción creada exitosamente');
        
        if (onInscriptionCreated) {
          onInscriptionCreated();
        }
        handleClose();
      } else {
        // Manejar errores del backend
        const errorMessage = response.message || 'Error al crear la inscripción';
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error creating course inscription:', error);
      
      // Manejar diferentes tipos de errores
      let errorMessage = 'Error al crear la inscripción. Por favor, intente nuevamente.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      studentId: '',
      studyId: '',
      studyPlanId: '',
      selectedCourses: [],
    });
    setAvailableSubjects([]);
    setAvailableCourses([]);
    setSelectedCoursesDetails([]);
    onClose();
  };

  const renderStepOne = () => (
    <VStack spacing={6} align="stretch" flex={1}>
      <Box textAlign="center" mb={4}>
        <Text fontSize="xl" fontWeight="bold" color="teal.600" mb={2}>
          Selección de Estudiante y Estudio
        </Text>
        <Text fontSize="md" color="gray.600">
          Seleccione el estudiante y el estudio para la inscripción
        </Text>
      </Box>
      
      <Flex direction={{ base: "column", lg: "row" }} gap={8} flex={1}>
        <Box flex={1}>
          <FormControl isRequired mb={6}>
            <FormLabel color={textColor} fontWeight="600" fontSize="lg" mb={3}>
              Estudiante
            </FormLabel>
            <Select
              placeholder="Seleccione un estudiante"
              value={formData.studentId}
              onChange={(e) => handleInputChange('studentId', e.target.value)}
              size="lg"
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "teal.400" }}
              _focus={{ borderColor: "teal.500", boxShadow: "0 0 0 1px teal.500" }}
            >
              {Array.isArray(students) && students.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.firstName || student.name} {student.lastName}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel color={textColor} fontWeight="600" fontSize="lg" mb={3}>
              Estudio
            </FormLabel>
            <Select
              placeholder="Seleccione un estudio"
              value={formData.studyId}
              onChange={(e) => handleInputChange('studyId', e.target.value)}
              size="lg"
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "teal.400" }}
              _focus={{ borderColor: "teal.500", boxShadow: "0 0 0 1px teal.500" }}
            >
              {Array.isArray(studies) && studies.map((study) => (
                <option key={study.id} value={study.id}>
                  {study.name || study.description}
                </option>
              ))}
            </Select>
          </FormControl>
        </Box>

        {formData.studentId && formData.studyId && (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <Box p={8} bg="green.50" borderRadius="xl" borderWidth="2px" borderColor="green.200" textAlign="center">
              <Text fontSize="lg" fontWeight="bold" color="green.700" mb={2}>
                ✓ Selección Completa
              </Text>
              <Text fontSize="md" color="green.600">
                Estudiante y estudio seleccionados correctamente.
              </Text>
              <Text fontSize="sm" color="green.500" mt={2}>
                En el siguiente paso elegí las asignaturas y la modalidad de pago de cada una.
              </Text>
            </Box>
          </Box>
        )}
      </Flex>
    </VStack>
  );

  const renderStepTwo = () => (
    <VStack spacing={6} align="stretch" flex={1}>
      <Box textAlign="center" mb={4}>
        <Text fontSize="xl" fontWeight="bold" color="teal.600" mb={2}>
          Selección de Materias y Configuración de Pagos
        </Text>
        <Text fontSize="md" color="gray.600">
          Seleccione las materias y configure las opciones de pago
        </Text>
      </Box>
      
      {/* Información del estudiante y estudio seleccionados */}
      <Box p={6} bg="gray.50" borderRadius="xl" borderWidth="1px" borderColor="gray.200">
        <Flex direction={{ base: "column", md: "row" }} gap={4} align="center">
          <Box flex={1}>
            <Text fontSize="md" color="gray.700" fontWeight="semibold">
              <Text as="span" color="teal.600">Estudiante:</Text>{" "}
              {(() => {
                const student = students.find(s => s.id === parseInt(formData.studentId));
                return student ? `${student.firstName || student.name || 'N/A'} ${student.lastName || ''}` : 'No encontrado';
              })()}
            </Text>
          </Box>
          <Box flex={1}>
            <Text fontSize="md" color="gray.700" fontWeight="semibold">
              <Text as="span" color="teal.600">Estudio:</Text>{" "}
              {(() => {
                const study = studies.find(s => s.id === parseInt(formData.studyId));
                return study ? (study.name || study.description || 'N/A') : 'No encontrado';
              })()}
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Lista de materias disponibles */}
      <Box flex={1}>
        <Text fontSize="lg" fontWeight="bold" mb={4} color={textColor}>
          Cursos Disponibles:
        </Text>
        
        {availableCourses.length > 0 ? (
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Seleccionar</Th>
                  <Th>Materia</Th>
                  <Th>Ciclo</Th>
                  <Th>Período</Th>
                  <Th>Horario</Th>
                  <Th>Inicio Inscripción</Th>
                  <Th>Fin Inscripción</Th>
                  <Th>Forma de Pago</Th>
                </Tr>
              </Thead>
              <Tbody>
                {availableCourses.map((course) => {
                  const courseDetail = selectedCoursesDetails ? selectedCoursesDetails.find(c => c.courseId === course.id) : null;
                  const isSelected = formData.selectedCourses.includes(course.id);
                  const hasDictatedCourse = typeof course.id === 'number' || /^\d+$/.test(String(course.id));
                  const courseMethods = course.coursePaymentMethods?.length
                    ? course.coursePaymentMethods
                    : paymentMethods.map((pm) => ({
                        id: pm.id,
                        paymentMethodId: pm.id,
                        paymentMethod: pm,
                        price: course.price || 0,
                      }));
                  
                  return (
                    <Tr 
                      key={course.id}
                      bg={isSelected ? "teal.50" : "white"}
                      _hover={{ bg: isSelected ? "teal.100" : "gray.50" }}
                    >
                      <Td>
                        <Checkbox
                          isChecked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                selectedCourses: [...prev.selectedCourses, course.id]
                              }));
                              // Agregar detalles por defecto para el curso
                              setSelectedCoursesDetails(prev => [
                                ...prev.filter(c => c.courseId !== course.id),
                                {
                                  courseId: course.id,
                                  paymentMethodId: course.coursePaymentMethods?.[0]?.paymentMethodId || null,
                                  amount: course.coursePaymentMethods?.[0]?.price || course.price || 0,
                                }
                              ]);
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                selectedCourses: prev.selectedCourses.filter(id => id !== course.id)
                              }));
                              // Remover detalles del curso
                              setSelectedCoursesDetails(prev => 
                                prev.filter(c => c.courseId !== course.id)
                              );
                            }
                          }}
                          colorScheme="teal"
                        />
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="semibold" fontSize="sm">
                            {course.subjectName || 'Sin nombre'}
                          </Text>
                          <Badge colorScheme={course.subject?.mandatory ? "red" : "blue"} size="sm">
                            {course.subject?.mandatory ? "Obligatorio" : "Optativo"}
                          </Badge>
                          {!hasDictatedCourse && (
                            <Text fontSize="xs" color="orange.500">Sin dictado: se inscribe igual, con la modalidad elegida</Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <Text fontSize="sm">{course.subjectCycle || 'N/A'}</Text>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="xs" color="gray.600">
                            Inicio: {course.startDate ? new Date(course.startDate).toLocaleDateString() : 'N/A'}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            Fin: {course.endDate ? new Date(course.endDate).toLocaleDateString() : 'N/A'}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.500">
                          Por definir
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs">
                          {course.inscriptionStartDate ? 
                            new Date(course.inscriptionStartDate).toLocaleDateString() : 
                            'N/A'
                          }
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs">
                          {course.inscriptionEndDate ? 
                            new Date(course.inscriptionEndDate).toLocaleDateString() : 
                            'N/A'
                          }
                        </Text>
                      </Td>
                      <Td>
                        {isSelected && courseMethods.length > 0 ? (
                          <Select
                            size="sm"
                            value={courseDetail?.paymentMethodId || ''}
                            onChange={(e) => {
                              const selectedMethod = courseMethods.find(
                                cpm => Number(cpm.paymentMethodId) === parseInt(e.target.value, 10)
                              );
                              handleCoursePaymentChange(course.id, 'paymentMethodId', parseInt(e.target.value, 10));
                              handleCoursePaymentChange(course.id, 'amount', selectedMethod?.price || course.price || 0);
                            }}
                            bg="white"
                            maxW="220px"
                          >
                            <option value="">Seleccionar</option>
                            {courseMethods.map((cpm) => {
                              // Mostrar opciones con formato "N cuotas: $X (total $Y)".
                              // Requerimiento cliente (Módulo 5): en la UI cada plan
                              // debe leerse como "1 cuota: $X" / "6 cuotas: $X".
                              const n =
                                cpm.paymentMethod?.installments ||
                                cpm.paymentMethod?.Installments ||
                                1;
                              const total = Number(cpm.price) || 0;
                              const per = calculateInstallmentAmount(total, n);
                              const label =
                                n === 1
                                  ? `1 cuota: $${total}`
                                  : `${n} cuotas: $${per} c/u (total $${total})`;
                              return (
                                <option key={cpm.id} value={cpm.paymentMethodId}>
                                  {label}
                                </option>
                              );
                            })}
                          </Select>
                        ) : isSelected ? (
                          <Text fontSize="xs" color="orange.500">
                            Sin métodos de pago configurados
                          </Text>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            Seleccione el curso
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <Box textAlign="center" py={12}>
            <Text color="gray.500" fontSize="lg" mb={4}>
              {formData.studyId ? 
                'No hay cursos disponibles para este estudio' : 
                'Seleccione un estudio para ver los cursos disponibles'
              }
            </Text>
            <Text color="gray.400" fontSize="sm">
              StudyId: {formData.studyId}, Step: {currentStep}
            </Text>
          </Box>
        )}
      </Box>
      
      {/* Resumen de cursos seleccionados */}
      {formData.selectedCourses.length > 0 && (
        <Box p={6} bg="blue.50" borderRadius="xl" borderWidth="2px" borderColor="blue.200">
          <Text fontSize="lg" fontWeight="bold" mb={3} color="blue.700">
            📋 Resumen de Inscripción
          </Text>
          <Flex direction={{ base: "column", md: "row" }} gap={4} align="center" justify="space-between">
            <Text fontSize="md" color="blue.600" fontWeight="medium">
              {formData.selectedCourses.length} curso{formData.selectedCourses.length > 1 ? 's' : ''} seleccionado{formData.selectedCourses.length > 1 ? 's' : ''}
            </Text>
            <Text fontSize="xl" color="blue.700" fontWeight="bold">
              Total: ${(() => {
                if (!selectedCoursesDetails || selectedCoursesDetails.length === 0) {
                  return '0.00';
                }
                const total = selectedCoursesDetails.reduce((sum, detail) => {
                  const amount = parseFloat(detail?.amount) || 0;
                  return sum + amount;
                }, 0);
                return (typeof total === 'number' && !isNaN(total)) ? total.toFixed(2) : '0.00';
              })()}
            </Text>
          </Flex>
        </Box>
      )}
    </VStack>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxHeight="90vh" overflow="hidden">
        <ModalHeader>
          <VStack spacing={4} align="stretch">
            <Text color={textColor} fontSize="24px" fontWeight="700">
              Nueva Inscripción
            </Text>
            
            {/* Stepper */}
            <Box px={4}>
              <Stepper 
                index={currentStep - 1} 
                colorScheme="teal"
                size="lg"
              >
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator>
                      <StepStatus
                        complete={<StepIcon />}
                        incomplete={<StepNumber />}
                        active={<StepNumber />}
                      />
                    </StepIndicator>

                    <Box flexShrink="0" ml={4}>
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </Box>

                    <StepSeparator />
                  </Step>
                ))}
              </Stepper>
            </Box>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody overflowY="auto" px={8} py={6}>
          <Flex direction="column" minHeight="400px">
            {currentStep === 1 ? renderStepOne() : renderStepTwo()}
          </Flex>
        </ModalBody>

        <ModalFooter px={8} py={6}>
          <HStack spacing={4} width="100%" justifyContent="space-between">
            <Box>
              {currentStep === 2 && (
                <Button 
                  variant="outline" 
                  onClick={handleBack}
                  size="lg"
                >
                  Volver
                </Button>
              )}
            </Box>
            
            <HStack spacing={3}>
              <Button 
                variant="ghost" 
                onClick={handleClose}
                size="lg"
              >
                Cancelar
              </Button>
              
              {currentStep === 1 ? (
                <Button 
                  bg={brandStars}
                  color="white"
                  _hover={{ bg: brandStars }}
                  onClick={handleContinue}
                  isDisabled={!formData.studentId || !formData.studyId}
                  size="lg"
                  px={8}
                >
                  Continuar
                </Button>
              ) : (
                <Button 
                  bg={brandStars}
                  color="white"
                  _hover={{ bg: brandStars }}
                  onClick={handleSubmit}
                  isDisabled={formData.selectedCourses.length === 0}
                  size="lg"
                  px={8}
                >
                  Crear Inscripción
                </Button>
              )}
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateInscriptionModal;
